/**
 * Buildings Energy Efficiency layer — segment-based policy application.
 * Applies local efficiency policies (reach codes, weatherization, other) on top of BAU pathway.
 * Demand response is applied downstream (after electrification) in policy time-series; see policyTimeSeriesService.
 *
 * Order: BAU therms/kWh → efficiency layer → (downstream: electrification → demand response → RNG, grid).
 *
 * Energy aggregation uses therm-equivalent conversion (1 therm = 29.3 kWh) for consistency.
 *
 * MATH: Policy effects are MULTIPLICATIVE on top of BAU segment split.
 * - BAU gives total therms/kWh by year (pathway). We split by segment:
 *   resTherms = bauTherms * resFracT, comTherms = bauTherms * (1 - resFracT), then
 *   existingResTherms = resTherms * existingResShare, newResTherms = resTherms * (1 - existingResShare), etc.
 * - Policy multipliers (all in 0..1, applied to segment demand):
 *   resReachMult, comReachMult (new stock), weatherizationMult, otherMult (existing + new), then DR on total kWh.
 * - Segment after policy: e.g. existingResThermsAfter = existingResTherms * weatherizationMult * otherMult.
 * - No BAU efficiency "multiplier" is applied here; BAU pathway already embodies BAU electrification/efficiency.
 *   This layer is LOCAL policy on top of that. So: policy effects are multiplicative on segment demand, not additive.
 */

import type { EnergyEfficiencyComponents } from '../types/policyImpact';

const BASE_YEAR = 2023;
const TARGET_YEAR = 2045;

/** 1 therm = 29.3 kWh; used to aggregate therms + kWh into a single energy quantity. */
const THERM_TO_KWH = 29.3;

/** Demand response cap (percent of total building kWh). Applied after electrification in policy time-series. */
export const DEMAND_RESPONSE_CAP_PERCENT = 5;

/** At 100% of existing stock reached, max effective demand reduction (0–1). */
const WEATHERIZATION_MAX_REDUCTION = 0.2; // 20% demand reduction when 100% stock retrofitted

/**
 * Commercial stock growth assumption when no real commercial series exists.
 * Existing commercial share in year t = 1 / (1 + g)^(t - BASE_YEAR). Documented simplification.
 */
export const COMMERCIAL_ANNUAL_GROWTH_RATE = 0.005; // 0.5% per year

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export interface EfficiencyLayerOptions {
  /** Residential share of total therms (0–1). From BuildingsEnergyData if available; default 0.6. */
  resFractionTherms?: number;
  /** Residential share of total kWh (0–1). Default same as resFractionTherms. */
  resFractionKwh?: number;
  /** Existing residential share of res energy in year t (0–1). If not provided, use default growth. From housing_units_bau when available. */
  existingResShareByYear?: (year: number) => number;
  /** Existing commercial share of com energy in year t (0–1). If not provided, use COMMERCIAL_ANNUAL_GROWTH_RATE. */
  existingComShareByYear?: (year: number) => number;
}

/** Fallback when no housing data: ~2% annual growth in housing units. Result in (0, 1]. */
function defaultExistingResShare(year: number): number {
  if (year <= BASE_YEAR) return 1;
  const growth = 0.02;
  const yearsOut = year - BASE_YEAR;
  const unitsAtYear = Math.pow(1 + growth, yearsOut);
  return Math.max(0, Math.min(1, 1 / unitsAtYear));
}

/** Fallback when no commercial growth series: simple documented growth (COMMERCIAL_ANNUAL_GROWTH_RATE). */
function defaultExistingComShare(year: number): number {
  if (year <= BASE_YEAR) return 1;
  const yearsOut = year - BASE_YEAR;
  const stockIndex = Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, yearsOut);
  return 1 / stockIndex;
}

/**
 * Ramp policy from base year to target year (linear).
 */
function rampFraction(year: number): number {
  if (year <= BASE_YEAR) return 0;
  if (year >= TARGET_YEAR) return 1;
  return (year - BASE_YEAR) / (TARGET_YEAR - BASE_YEAR);
}

/**
 * Apply efficiency layer for a single year. Used by both time-series and 2045 summary.
 * Returns efficiency-adjusted therms and kWh and the effective efficiency percent for that year.
 */
export function applyEfficiencyForYear(
  bauTherms: number,
  bauKwh: number,
  year: number,
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): { thermsAfter: number; kwhAfter: number; efficiencyPercentTherms: number; efficiencyPercentKwh: number } {
  const ramp = rampFraction(year);
  const resFracT = clamp(options?.resFractionTherms ?? 0.6, 0, 1);
  const resFracK = options?.resFractionKwh ?? resFracT;
  const existingRes = options?.existingResShareByYear?.(year) ?? defaultExistingResShare(year);
  const existingCom = options?.existingComShareByYear?.(year) ?? defaultExistingComShare(year);

  const newResShare = 1 - existingRes;
  const newComShare = 1 - existingCom;

  const resTherms = bauTherms * resFracT;
  const comTherms = bauTherms * (1 - resFracT);
  const resKwh = bauKwh * resFracK;
  const comKwh = bauKwh * (1 - resFracK);

  const existingResTherms = resTherms * existingRes;
  const newResTherms = resTherms * newResShare;
  const existingComTherms = comTherms * existingCom;
  const newComTherms = comTherms * newComShare;
  const existingResKwh = resKwh * existingRes;
  const newResKwh = resKwh * newResShare;
  const existingComKwh = comKwh * existingCom;
  const newComKwh = comKwh * newComShare;

  const resReachMult = 1 - (components.residentialReachCodePercent / 100) * ramp;
  const comReachMult = 1 - (components.commercialReachCodePercent / 100) * ramp;
  const weatherizationReduction = (components.weatherizationPercent / 100) * ramp * WEATHERIZATION_MAX_REDUCTION;
  const weatherizationMult = 1 - clamp(weatherizationReduction, 0, 1);
  const otherMult = 1 - (components.otherEfficiencyPercent / 100) * ramp;

  const existingResThermsAfter = existingResTherms * weatherizationMult * otherMult;
  const newResThermsAfter = newResTherms * resReachMult * otherMult;
  const existingComThermsAfter = existingComTherms * weatherizationMult * otherMult;
  const newComThermsAfter = newComTherms * comReachMult * otherMult;

  const existingResKwhAfter = existingResKwh * weatherizationMult * otherMult;
  const newResKwhAfter = newResKwh * resReachMult * otherMult;
  const existingComKwhAfter = existingComKwh * weatherizationMult * otherMult;
  const newComKwhAfter = newComKwh * comReachMult * otherMult;

  let thermsAfter =
    existingResThermsAfter + newResThermsAfter + existingComThermsAfter + newComThermsAfter;
  let kwhAfter =
    existingResKwhAfter + newResKwhAfter + existingComKwhAfter + newComKwhAfter;

  // Demand response is applied after electrification in policy time-series (policyTimeSeriesService), not here.

  thermsAfter = Math.max(0, thermsAfter);
  kwhAfter = Math.max(0, kwhAfter);

  const efficiencyPercentTherms =
    bauTherms > 0 ? (1 - thermsAfter / bauTherms) * 100 : 0;
  const efficiencyPercentKwh =
    bauKwh > 0 ? (1 - kwhAfter / bauKwh) * 100 : 0;

  return {
    thermsAfter,
    kwhAfter,
    efficiencyPercentTherms: clamp(efficiencyPercentTherms, 0, 100),
    efficiencyPercentKwh: clamp(efficiencyPercentKwh, 0, 100),
  };
}

/** Per-policy contribution to combined energy reduction (therm-equivalent kWh) for charts. Marginal: reduction with all - reduction with this policy set to 0. */
export interface EfficiencyContributions {
  weatherization: number;
  residentialReach: number;
  commercialReach: number;
  demandResponse: number;
  other: number;
}

function componentsWithPolicyZeroed(
  components: EnergyEfficiencyComponents,
  key: keyof EnergyEfficiencyComponents
): EnergyEfficiencyComponents {
  const c = { ...components, [key]: 0 };
  return c;
}

/**
 * Get efficiency-adjusted demand and per-policy contributions for a single year.
 * Contributions are marginal reductions in combined energy (therm-equivalent kWh) vs BAU.
 */
export function applyEfficiencyForYearWithContributions(
  bauTherms: number,
  bauKwh: number,
  year: number,
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): {
  thermsAfter: number;
  kwhAfter: number;
  contributions: EfficiencyContributions;
} {
  const full = applyEfficiencyForYear(bauTherms, bauKwh, year, components, options);
  const combinedAfter = full.thermsAfter * THERM_TO_KWH + full.kwhAfter;

  const keys: (keyof EnergyEfficiencyComponents)[] = [
    'weatherizationPercent',
    'residentialReachCodePercent',
    'commercialReachCodePercent',
    'demandResponsePercent',
    'otherEfficiencyPercent',
  ];
  const contributionKeys: (keyof EfficiencyContributions)[] = [
    'weatherization',
    'residentialReach',
    'commercialReach',
    'demandResponse',
    'other',
  ];
  const contributions: EfficiencyContributions = {
    weatherization: 0,
    residentialReach: 0,
    commercialReach: 0,
    demandResponse: 0,
    other: 0,
  };
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const ck = contributionKeys[i];
    if ((components[k] ?? 0) === 0) continue;
    const without = applyEfficiencyForYear(
      bauTherms,
      bauKwh,
      year,
      componentsWithPolicyZeroed(components, k),
      options
    );
    const combinedWithout = without.thermsAfter * THERM_TO_KWH + without.kwhAfter;
    contributions[ck] = Math.max(0, combinedWithout - combinedAfter);
  }
  return {
    thermsAfter: full.thermsAfter,
    kwhAfter: full.kwhAfter,
    contributions,
  };
}

export interface EfficiencyLayerResult {
  thermsByYear: number[];
  kwhByYear: number[];
  years: number[];
  /** Combined efficiency % in target year (2045), energy-weighted. */
  totalEfficiencyPercent2045: number;
}

/**
 * Apply efficiency layer to BAU pathway time series.
 * Returns efficiency-adjusted therms and kWh by year.
 */
export function applyEfficiencyLayer(
  bauThermsByYear: number[],
  bauKwhByYear: number[],
  years: number[],
  components: EnergyEfficiencyComponents,
  options?: EfficiencyLayerOptions
): EfficiencyLayerResult {
  const thermsByYear = years.map((year, i) => {
    const bauT = bauThermsByYear[i] ?? 0;
    const bauK = bauKwhByYear[i] ?? 0;
    const out = applyEfficiencyForYear(bauT, bauK, year, components, options);
    return out.thermsAfter;
  });
  const kwhByYear = years.map((year, i) => {
    const bauT = bauThermsByYear[i] ?? 0;
    const bauK = bauKwhByYear[i] ?? 0;
    const out = applyEfficiencyForYear(bauT, bauK, year, components, options);
    return out.kwhAfter;
  });

  const idx2045 = years.indexOf(TARGET_YEAR);
  const bauT2045 = idx2045 >= 0 ? bauThermsByYear[idx2045] ?? 0 : 0;
  const bauK2045 = idx2045 >= 0 ? bauKwhByYear[idx2045] ?? 0 : 0;
  const out2045 = applyEfficiencyForYear(bauT2045, bauK2045, TARGET_YEAR, components, options);
  const combinedBefore = bauT2045 * THERM_TO_KWH + bauK2045;
  const combinedAfter = out2045.thermsAfter * THERM_TO_KWH + out2045.kwhAfter;
  const totalEfficiencyPercent2045 =
    combinedBefore > 0 ? (1 - combinedAfter / combinedBefore) * 100 : 0;

  return {
    thermsByYear,
    kwhByYear,
    years,
    totalEfficiencyPercent2045: clamp(totalEfficiencyPercent2045, 0, 100),
  };
}

/**
 * Compute effective total efficiency percent from components (for display when in policy mode).
 * Uses 2045 with default segment shares. Energy aggregation uses therm-equivalent conversion (1 therm = 29.3 kWh) for consistency.
 */
export function computeTotalEfficiencyPercentFromComponents(
  components: EnergyEfficiencyComponents,
  bauTherms2045: number,
  bauKwh2045: number,
  options?: EfficiencyLayerOptions
): number {
  const out = applyEfficiencyForYear(
    bauTherms2045,
    bauKwh2045,
    TARGET_YEAR,
    components,
    options
  );
  const combinedBefore = bauTherms2045 * THERM_TO_KWH + bauKwh2045;
  const combinedAfter = out.thermsAfter * THERM_TO_KWH + out.kwhAfter;
  if (combinedBefore <= 0) return 0;
  return clamp((1 - combinedAfter / combinedBefore) * 100, 0, 100);
}
